import { GENERIC_PACK, type StatePack, type StateTenant } from './types'
import { ny } from './ny'
import { md } from './md'
import { az } from './az'
import { getTenant } from './tenants'
import { slugToCode, codeToSlug } from '@/lib/ic3'
import { getStateAgencies } from './state-agencies'

const STATE_PACKS: Record<string, StatePack> = {
  ny,
  md,
  az,
}

/**
 * Resolve a curated pack from either a 2-letter code ("az") or a name slug
 * ("arizona"). For states without a fully curated pack, returns a pack
 * built from the verified state-agencies lookup so every state page has
 * accurate "Where to report" entries instead of the generic federal-only
 * fallback.
 */
export function getStatePack(slug: string | undefined | null): StatePack {
  if (!slug) return GENERIC_PACK
  const direct = STATE_PACKS[slug.toLowerCase()]
  if (direct) return direct
  const code = slugToCode(slug)
  if (code) {
    const byCode = STATE_PACKS[code.toLowerCase()]
    if (byCode) return byCode
    return buildLookupPack(code)
  }
  return GENERIC_PACK
}

/**
 * Build a pack for a state without a hand-curated file: state-specific
 * agencies (from the verified lookup) followed by the federal fallbacks
 * (IC3, FTC, AARP). State name comes from the IC3 slug map so the page
 * title still renders correctly.
 */
function buildLookupPack(code: string): StatePack {
  const stateAgencies = getStateAgencies(code)
  if (stateAgencies.length === 0) return GENERIC_PACK
  const slug = codeToSlug(code) ?? code.toLowerCase()
  return {
    ...GENERIC_PACK,
    slug,
    name: null,
    agencies: [...stateAgencies, ...GENERIC_PACK.agencies],
  }
}

export function listStatePacks(): StatePack[] {
  return Object.values(STATE_PACKS)
}

/**
 * Build the live pack used by StateProvider, applying any tenant overlay
 * resolved from the URL's `?tenant=` query param.
 */
export function buildLivePack(slug: string | null, tenantId: string | null): StatePack {
  const base = getStatePack(slug)
  if (!tenantId) return base
  const tenant = getTenant(tenantId)
  if (!tenant) return base
  return { ...base, tenant }
}

export type { StatePack, StateAgency, StateTopScam, StateTenant } from './types'
export { GENERIC_PACK } from './types'
export { listTenants, getTenant } from './tenants'
